import { useState } from 'react'

import {
  useAllSamples,
  useCanCall,
  useMe,
  useSample,
  useSampleReport,
  useSampleReports,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { SAMPLES } from '@ctcells/fixtures'
import { errorMessage, t } from '@ctcells/i18n'

import { DEMO_SAMPLES } from '../demoData.ts'
import { useScreenAccess } from '../screenAccess.ts'

import { useRunningDeviceKey } from './followRunningDevice.ts'
import {
  MonitoringSampleDrawer,
  type DrawerChambers,
  type DrawerDetail,
  type DrawerMember,
} from './MonitoringSampleDrawer.tsx'
import { drawerChambersOf, drawerReportOf } from './monitoringSampleDrawerModel.ts'
import { useSampleFlow } from './useSampleFlow.ts'

/**
 * 모니터링 시료 서랍 컨테이너 — 라우트(`MonitoringRoute`)가 서랍 자리에 꽂는다.
 *
 * | 자료 | 경로 | 못 보면 |
 * | --- | --- | --- |
 * | 챔버 | 카드 격자와 **같은 흐름**(`useSampleFlow` · 같은 쿼리 인자 ⇒ 요청 0건 추가) | 챔버 구역에만 사유 |
 * | **주** | `GET /samples/{id}` | 시료 상세 구역에만 사유 — 서랍은 선다(viewer 403) |
 * | 부가 | `GET /reports/{id}` — 진행 · 분주(카드가 같은 캐시 키로 이미 받는다) | 그 구역에만 사유 |
 *
 * 🔴 **흐름을 고르는 규칙은 `MonitoringContainer` 와 같다**(작업 고르기 · 시료 목록 권한 3값 · 조회 상한).
 *    같은 훅에 같은 인자를 넘겨 react-query 가 요청을 합친다 — 서랍이 새 요청을 내지 않는다.
 *    ⚠ 두 곳이 갈리면 카드와 서랍이 다른 챔버를 말한다. 규칙을 바꿀 땐 둘을 같이 바꾼다.
 * 🔴 **못 부르는 조회는 쏘지 않는다**(viewer — `getSample` · `getSampleReport` 가 없다). 운영권을 모르면(null) 쏜다.
 * 🔴 폴링 없음 — 갱신은 구독(`sample_lifecycle`)이 `keys.samples` · `keys.reports` 를 무효화할 때만 온다.
 */
export interface MonitoringSampleDrawerContainerProps {
  sampleId: string
  onClose: () => void
}

export function MonitoringSampleDrawerContainer(props: MonitoringSampleDrawerContainerProps) {
  return dataSourceFor('modules') === 'demo' ? (
    <ChambersDemo {...props} />
  ) : (
    <ChambersLive {...props} />
  )
}

function ChambersDemo(props: MonitoringSampleDrawerContainerProps) {
  const runningDevice = useRunningDeviceKey()
  const flow = useSampleFlow({
    samples: DEMO_SAMPLES,
    skipTimes: true,
    ...(runningDevice === undefined ? {} : { runningDevice }),
  })
  return <DetailPart {...props} chambers={drawerChambersOf(flow, props.sampleId)} />
}

function ChambersLive(props: MonitoringSampleDrawerContainerProps) {
  const runningDevice = useRunningDeviceKey()
  const { operations } = useMe()
  const canListSamples = useCanCall('listSamples')
  const samplesLocked =
    operations !== null && !canListSamples ? t('monitoring.samplesLocked') : undefined
  const samples = useAllSamples({ skip: samplesLocked !== undefined })
  const notice =
    samplesLocked ??
    (samples.isPending
      ? t('state.loading')
      : samples.isError
        ? errorMessage(samples.error)
        : undefined)
  const flow = useSampleFlow({
    samples: samples.data?.items,
    ...(notice === undefined ? {} : { notice }),
    ...(runningDevice === undefined ? {} : { runningDevice }),
  })
  return <DetailPart {...props} chambers={drawerChambersOf(flow, props.sampleId)} />
}

type DetailPartProps = MonitoringSampleDrawerContainerProps & { chambers: DrawerChambers }

/**
 * 같은 카드(레시피 시료 묶음)의 **다른 시료 번호** — 카드와 같은 묶음(`bundle.sampleIds`)에서 연 시료를 뺀 것.
 * 🔴 묶음이 아니면(시료 하나 · 챔버 구역 없음) 빈 배열 — 서랍은 종전대로 시료 하나다.
 */
function otherSampleIds(chambers: DrawerChambers, sampleId: string): readonly string[] {
  if (chambers.kind !== 'bundle') return []
  return (chambers.bundle.sampleIds ?? []).filter((id) => id !== sampleId)
}

function DetailPart(props: DetailPartProps) {
  return dataSourceFor('samples') === 'demo' ? <DetailDemo {...props} /> : <DetailLive {...props} />
}

/** demo — fixture 시료. 보고서 · 정정 버튼은 서랍이 두지 않는다(`hideReport` · `hideCorrection`) */
function DetailDemo({ sampleId, onClose, chambers }: DetailPartProps) {
  const access = useScreenAccess()
  const sample = SAMPLES.find((item) => item.sample_id === sampleId)
  const members: DrawerMember[] = otherSampleIds(chambers, sampleId).map((id) => {
    const found = SAMPLES.find((item) => item.sample_id === id)
    return {
      sampleId: id,
      detail:
        found === undefined
          ? { kind: 'notice', text: t('state.empty.samples') }
          : { kind: 'ready', sample: found },
      report: { kind: 'none' },
    }
  })
  const detail: DrawerDetail =
    sample === undefined
      ? { kind: 'notice', text: t('state.empty.samples') }
      : { kind: 'ready', sample }
  return (
    <MonitoringSampleDrawer
      access={access}
      chambers={chambers}
      detail={detail}
      onClose={onClose}
      members={members}
      report={{ kind: 'none' }}
      sampleId={sampleId}
    />
  )
}

function DetailLive({ sampleId, onClose, chambers }: DetailPartProps) {
  const access = useScreenAccess()
  const { operations } = useMe()
  const canGetSample = useCanCall('getSample')
  const canGetReport = useCanCall('getSampleReport')
  /* 🔴 3값 — 운영권을 모르면(null) 쏜다. 알고 없으면 안 쏜다(403 이 확정이다) */
  const sampleLocked = operations !== null && !canGetSample
  const reportLocked = operations !== null && !canGetReport
  const lockedText = errorMessage({ error_code: 'INSUFFICIENT_ROLE' })

  const sample = useSample({ sampleId: sampleLocked ? undefined : sampleId })
  const report = useSampleReport({ sampleId: reportLocked ? undefined : sampleId })
  /*
   * 🔴 **묶음의 다른 시료**(eric 2026-10-14 「시료 4에.. 두개가 보여야」) — 시료 값은 카드가 이미 받은 목록(`GET /samples`)에서,
   *    진행 · 분주는 카드가 같은 캐시 키로 받는 보고서(`useSampleReports`)에서 온다 ⇒ 요청이 새로 늘지 않는다.
   */
  const others = otherSampleIds(chambers, sampleId)
  const canListSamples = useCanCall('listSamples')
  const listLocked = operations !== null && !canListSamples
  const list = useAllSamples({ skip: listLocked || others.length === 0 })
  const otherReports = useSampleReports({ sampleIds: others, skip: reportLocked })
  const members: DrawerMember[] = others.map((id) => {
    const found = list.data?.items.find((item) => item.sample_id === id)
    const otherReport = otherReports.byId.get(id)
    return {
      sampleId: id,
      detail:
        found !== undefined
          ? { kind: 'ready', sample: found }
          : {
              kind: 'notice',
              text: listLocked
                ? lockedText
                : list.isError
                  ? errorMessage(list.error)
                  : list.isPending
                    ? t('state.loading')
                    : t('state.empty.samples'),
            },
      report: drawerReportOf({
        locked: reportLocked,
        lockedText,
        pending: otherReport === undefined && otherReports.isPending,
        error: otherReport === undefined ? (otherReports.error ?? null) : null,
        data: otherReport,
      }),
    }
  })
  /* 「보고서」 대화 — 서랍이 이미 받은 보고서를 그대로 보인다(요청 추가 없음) */
  const [reportOpen, setReportOpen] = useState(false)

  const detail: DrawerDetail = sampleLocked
    ? { kind: 'notice', text: lockedText }
    : sample.isError
      ? {
          kind: 'notice',
          text: errorMessage(sample.error),
          /* 403 은 «못 볼 사람»이라는 사실이지 고장이 아니다 */
          tone: sample.error.status === 403 ? undefined : 'error',
        }
      : sample.data === undefined
        ? { kind: 'loading' }
        : {
            kind: 'ready',
            sample: sample.data,
            onOpenReport: () => {
              setReportOpen(true)
            },
            ...(reportOpen
              ? {
                  reportPanel: {
                    sampleId,
                    report: report.data,
                    loading: report.isPending,
                    error: report.error === null ? undefined : errorMessage(report.error),
                    onClose: () => {
                      setReportOpen(false)
                    },
                  },
                }
              : {}),
          }

  return (
    <MonitoringSampleDrawer
      access={access}
      chambers={chambers}
      detail={detail}
      members={members}
      onClose={onClose}
      report={drawerReportOf({
        locked: reportLocked,
        lockedText,
        pending: report.isPending,
        error: report.error,
        data: report.data,
      })}
      sampleId={sampleId}
    />
  )
}
